/**
 * useChartTheme — resolves the active @oxbo/tokens palette off the document
 * root so Plotly axes/fonts/gridlines follow the theme (dark/light/tenant)
 * without hardcoding a colour. Re-reads when theme/tenant/palette change.
 *
 * Charter chart rules (§15): transparent paper/plot background (the card
 * supplies the surface), theme-neutral axes, monospace tick fonts. PNG export
 * with a transparent background is handled in ChartFrame's Plotly config.
 */
import { useMemo } from "react";
import { useAppSelector } from "@app/hooks";

export interface ChartTheme {
  ink: string;
  dim: string;
  line: string;
  grid: string;
  fontSans: string;
  fontMono: string;
  brand: string;
  good: string;
  warn: string;
  crit: string;
  oxboM: string;
  oxboP: string;
  oxboZ: string;
  oxboU: string;
}

function readVar(name: string, fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

export function useChartTheme(): ChartTheme {
  // Depend on the prefs that change the resolved tokens so the memo re-runs.
  const theme = useAppSelector((s) => s.prefs.theme);
  const tenant = useAppSelector((s) => s.prefs.tenant);

  return useMemo<ChartTheme>(
    () => ({
      ink: readVar("--ink", "#eef0f3"),
      dim: readVar("--dim", "#aeb4c0"),
      line: readVar("--line", "#2a3140"),
      grid: readVar("--line", "#2a3140"),
      fontSans: readVar("--font-sans", "system-ui, sans-serif"),
      fontMono: readVar("--font-mono", "ui-monospace, monospace"),
      brand: readVar("--brand", "#c760c7"),
      good: readVar("--good", "#3ecf8e"),
      warn: readVar("--warn", "#f0b429"),
      crit: readVar("--crit", "#f0576a"),
      oxboM: readVar("--oxbo-m", "#9b8cff"),
      oxboP: readVar("--oxbo-p", "#f5a524"),
      oxboZ: readVar("--oxbo-z", "#4a9fe0"),
      oxboU: readVar("--oxbo-u", "#4fd0c0"),
    }),
    // `theme`/`tenant` aren't referenced in the body but ARE the trigger: they
    // change which token values `readVar` resolves off the document root, so we
    // must re-read when they change. This is an intentional external dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, tenant],
  );
}
